/**
 * ClipDockBar — pure collapsed dock row: latest clip preview + Copy/Send/expand (host wires callbacks).
 * SECURITY: `latest.content` is clipboard plaintext — render it, never log it; masked in publicMode / when `latest.sensitive` is set.
 */

import { Check, Clipboard, Copy01, Send01 } from "@untitledui/icons";
import { formatRelativeTime } from "../../utils/format";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { IconButton } from "../IconButton";
import { Text } from "../Text";
import type { DockClip } from "./ClipboardCapabilities";
import styles from "./ClipDockBar.module.css";

const MASK = "••••••••••";

export interface ClipDockBarLabels {
  empty: string;
  copy: string;
  copied: string;
  send: string;
  expand: string;
  from: (label: string) => string;
}

export interface ClipDockBarProps {
  latest: DockClip | null;
  publicMode?: boolean;
  copied?: boolean;
  pulsing?: boolean;
  /** Disable Send while a read is in-flight or the confirm popover is open (double-click guard). */
  sendDisabled?: boolean;
  /** Disable Copy while a writeText is in-flight (double-click guard). */
  copyDisabled?: boolean;
  onCopy: () => void;
  onSend: () => void;
  onExpand: () => void;
  labels: ClipDockBarLabels;
}

export function ClipDockBar({
  latest,
  publicMode,
  copied,
  pulsing,
  sendDisabled,
  copyDisabled,
  onCopy,
  onSend,
  onExpand,
  labels,
}: ClipDockBarProps) {
  const masked = publicMode || latest?.sensitive === true;
  const preview = latest ? (masked ? MASK : latest.content) : labels.empty;
  // When unmasked, let a SR user hear the clip's origin context on the tap region;
  // otherwise keep the bare expand label (masked preview leaks nothing).
  const expandLabel =
    latest && !masked ? `${labels.expand} — ${labels.from(latest.originLabel)}` : labels.expand;

  return (
    <div className={styles.bar} data-pulsing={pulsing || undefined}>
      <Icon as={Clipboard} size="sm" tone="muted" />
      <button
        type="button"
        className={styles.tapRegion}
        aria-label={expandLabel}
        onClick={onExpand}
      >
        <span className={styles.preview} aria-hidden="true">
          {preview}
        </span>
        {latest ? (
          <Text className={styles.meta} size="xs" tone="tertiary" aria-hidden="true">
            {`${labels.from(latest.originLabel)} · ${formatRelativeTime(latest.timestamp)}`}
          </Text>
        ) : null}
      </button>
      {latest ? (
        <IconButton
          icon={copied ? Check : Copy01}
          label={copied ? labels.copied : labels.copy}
          size="s"
          disabled={masked || copyDisabled}
          onClick={onCopy}
        />
      ) : null}
      <Button
        variant="primary"
        size="s"
        leadingIcon={Send01}
        disabled={sendDisabled}
        onClick={onSend}
      >
        {labels.send}
      </Button>
    </div>
  );
}
